import React, { useState, useEffect } from "react";
import { MdArrowBack } from "react-icons/md";
import { FcGoogle } from "react-icons/fc";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";

const scrollableStyle = {
  maxHeight: "80vh",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255,130,44,0.7) transparent",
};

const ScrollbarStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: rgba(255, 130, 44, 0.7);
      border-radius: 3px;
      border: 1px solid transparent;
    }
  `}</style>
);

const SuccessDialog = ({ message = "Subscription verified successfully!", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000); // auto close after 3 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-80 max-w-full shadow-lg font-semibold text-green-700 text-lg text-center select-none">
        {message}
      </div>
    </div>
  );
};

export default function SubscriptionDialog({ onClose }) {
  const GUMROAD_CHECKOUT = process.env.REACT_APP_GUMROAD_CHECKOUT;


  const [licenseKey, setLicenseKey] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState("");
  const [loadingVerification, setLoadingVerification] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const auth = getAuth();
  const provider = new GoogleAuthProvider();

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUser(currentUser);
      setIsLoggedIn(true);
    }
  }, [auth]);

  const verifySubscriptionAfterLogin = async (user) => {
    setLoadingVerification(true);
    setVerificationError("");
    try {
      const email = user.email;
      const user_id = user.uid;
      const license_key = licenseKey;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/verify-license`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, user_id, license_key }),
      });
      const data = await response.json();
      if (response.ok && data.active) {
        setVerificationResult(true);
        setVerificationError("");
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowSuccessDialog(false);
          onClose();
        }, 3000);
      } else {
        setVerificationResult(false);
        setVerificationError("Subscription not active or license invalid.");
      }
    } catch {
      setVerificationResult(false);
      setVerificationError("Verification failed. Please try later.");
    } finally {
      setLoadingVerification(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingLogin(true);
    setVerificationError("");
    try {
      const result = await signInWithPopup(auth, provider);
      const userData = result.user;
      setUser(userData);
      setIsLoggedIn(true);
    } catch (error) {
      setVerificationError("Login failed. Please try again.");
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleVerify = async () => {
    if (!user || !licenseKey) return;
    await verifySubscriptionAfterLogin(user);
  };

  return (
    <>
      <ScrollbarStyles />
      <div
        className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 custom-scrollbar"
          style={scrollableStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 left-4 text-gray-500 hover:text-gray-900 transition"
            aria-label="Close dialog"
          >
            <MdArrowBack size={24} />
          </button>

          <h2 className="text-2xl font-bold mb-6 text-center">
            Subscribe and Verify
          </h2>

          {/* Step 1 */}
          <section className="mb-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Step 1: Purchase Subscription</h3>
            <p className="mb-4 text-gray-600">
              Click the button below to subscribe via Gumroad's secure checkout.
            </p>
            <a
              href={GUMROAD_CHECKOUT}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full text-center px-4 py-3 bg-[#FF822C] text-white rounded-md font-semibold hover:bg-[#ff9349] transition"
            >
              Purchase Subscription
            </a>
          </section>

          {/* Step 2 */}
          <section className="mb-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Step 2: Login to Verify</h3>
            <button
              onClick={handleGoogleLogin}
              disabled={isLoggedIn || loadingLogin}
              className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md font-semibold ${
                isLoggedIn || loadingLogin
                  ? "bg-gray-300 cursor-not-allowed text-gray-700"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              }`}
            >
              <FcGoogle size={28} />
              {loadingLogin ? "Signing in..." : isLoggedIn ? "Already logged in" : "Login with Google"}
            </button>
            {isLoggedIn && (
              <div className="mt-4 text-sm text-green-600">Logged in as: {user?.email}</div>
            )}
          </section>

          {/* Step 3 */}
          <section className="mb-4 text-center">
            <h3 className="text-lg font-semibold mb-2">Step 3: Enter License Key & Verify</h3>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="Enter license key"
              className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={handleVerify}
              disabled={!isLoggedIn || !licenseKey || loadingVerification}
              className={`w-full px-4 py-3 rounded-md font-semibold ${
                !isLoggedIn || !licenseKey || loadingVerification
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#FF822C] text-white hover:bg-[#ff9349]"
              }`}
            >
              {loadingVerification ? "Verifying..." : "Verify License"}
            </button>
            {verificationResult && verificationResult === true && (
              <p className="mt-4 text-green-600 font-medium">You are all set!</p>
            )}
            {verificationResult === false && (
              <p className="mt-4 text-red-600 font-medium">Something went wrong.</p>
            )}
            {verificationError && (
              <p className="mt-4 text-red-600 font-medium">{verificationError}</p>
            )}
          </section>
        </div>
      </div>

      {showSuccessDialog && (
        <SuccessDialog
          message="Subscription verified successfully!"
          onClose={() => setShowSuccessDialog(false)}
        />
      )}
    </>
  );
}
